import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_plan_tasks_for_date,
  API_get_role_names,
  API_get_now_tasks,
} from "../../../constants";
import { getStoreNow } from "./LiveDayLaborCore";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { DayGanttMap, axisBoundsFor, buildMapEntries } from "./DayGanttMap";
import KioskTasksPanel from "./KioskTasksPanel";
import NowTable from "../plan/tasks_hub/NowTable";
import TaskDrawerHost from "../plan/tasks_hub/TaskDrawerHost";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";

// The Tasks tab of Live Action, in two modes:
//
//   manager (kiosk=false): the logged-in manager's view. The day strip up top
//   shows the shape of the day; under it is the SAME Now table and detail
//   drawer the Tasks page uses, so Assign, Work on it, Validate, the thread
//   and the timeline are one implementation. The manager acts as themselves.
//
//   kiosk (kiosk=true): a shared screen with no logged-in person.
//   KioskTasksPanel first: the person identifies (name, or PIN when the store
//   requires one) and works their own Now list through the same checklist
//   modal, via the kiosk endpoints acting for them. The day strip sits under
//   that list, cut down to the person's own bars, because nobody walking past
//   can act on a bar until they have identified.

function LiveActionTasksPanel(props) {
  const {
    kiosk = false,
    storeId,
    homeCompany,
    startQhnum,
    storeTimezone,
    actualSchedule,
    actualUsedSchedule,
    pinRequired = false,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [planTasks, setPlanTasks] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  // Manager mode only: the Now payload behind the table.
  const [nowData, setNowData] = useState(null);
  const [nowError, setNowError] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const drawerHost = useRef(null);

  // Tick every minute so todayStr advances when the business day boundary crosses
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setClockTick((c) => c + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Compute business date (same logic as LiveDayLaborCore)
  const todayStr = useMemo(() => {
    const storeNow = getStoreNow(storeTimezone);
    const now = dayjs(storeNow);
    const currentHour = now.hour() + now.minute() / 60;
    return currentHour < startQhnum
      ? now.subtract(1, "day").format("YYYY-MM-DD")
      : now.format("YYYY-MM-DD");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeTimezone, startQhnum, clockTick]);

  // A slow response from the previous store, or from before a Done toggle,
  // must not land under the current one.
  const nowContextRef = useRef(null);
  const fetchNow = useCallback(async () => {
    if (!storeId) return;
    const context = `${storeId}:${showDone}:${showUpcoming}`;
    nowContextRef.current = context;
    try {
      const res = await getRightworkAPI(API_get_now_tasks, {
        store_id: storeId,
        include_done: showDone ? "true" : "false",
        include_upcoming: showUpcoming ? "true" : "false",
      });
      if (nowContextRef.current !== context) return;
      setNowData(res.status === 200 ? res.data?.redux?.payload || null : null);
      setNowError(res.status !== 200);
    } catch (error) {
      console.error("LiveActionTasksPanel: Error fetching now tasks", error);
      if (nowContextRef.current !== context) return;
      setNowData(null);
      setNowError(true);
    }
  }, [storeId, showDone, showUpcoming]);

  // Resolves false when the day payload could not be refetched (a checklist
  // save reports that rather than closing onto stale answers).
  const fetchData = useCallback(
    async (showLoading = false) => {
      if (!storeId) {
        // Nothing to fetch, but the panel must not sit on a spinner forever.
        setIsLoading(false);
        setRefreshing(false);
        return false;
      }
      if (showLoading) setIsLoading(true);

      const axiosConfig = getDefaultAxiosConfig();

      try {
        const jobs = [
          axios.get(
            API_get_plan_tasks_for_date +
              "?store_id=" +
              storeId +
              "&date=" +
              todayStr,
            axiosConfig
          ),
          axios.get(API_get_role_names + storeId, axiosConfig),
        ];
        if (!kiosk) jobs.push(fetchNow());
        const [planTasksRes, roleNamesRes] = await Promise.all(jobs);

        let planTaskData = planTasksRes.data;
        if (planTaskData?.redux?.payload?.plan_tasks) {
          planTaskData = planTaskData.redux.payload.plan_tasks;
        }
        setPlanTasks(planTaskData || []);
        setRoleNames(roleNamesRes.data || []);
        return true;
      } catch (error) {
        console.error("LiveActionTasksPanel: Error fetching data", error);
        // Drop whatever is on screen: after a store switch these rows belong to
        // the previous location, and showing one store's tasks under another
        // store's name is worse than showing none.
        setPlanTasks([]);
        return false;
      } finally {
        setIsLoading(false);
        setRefreshing(false);
      }
    },
    [storeId, todayStr, kiosk, fetchNow]
  );

  // Fetch on mount and when store changes
  // The full spinner is for the first load and a store switch; flipping a
  // Show toggle refetches in place and keeps the rows on screen.
  const loadedStoreRef = useRef(null);
  useEffect(() => {
    const showLoading = loadedStoreRef.current !== storeId;
    loadedStoreRef.current = storeId;
    fetchData(showLoading);
  }, [fetchData, storeId]);

  // Auto-refresh. A shared kiosk screen is a queue people act on, and a
  // manager keeps this tab open next to the labor chart: once a minute keeps
  // the rows honest without hammering the server.
  useEffect(() => {
    const interval = setInterval(() => fetchData(false), 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Refresh when browser tab becomes visible again
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchData(false);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchData]);

  // Today's feed is everything that can be done today: today's occurrences,
  // this month's / week's "any time" work (anchored on the period's last day,
  // so its date is not today) and open one-offs from earlier dates. Keep all
  // of it; the backend already scoped the feed to today.
  const todayPlanTasks = useMemo(() => {
    return (planTasks || []).map((task) => ({
      ...task,
      plan_task_assignments: task.plan_task_assignments || [],
    }));
  }, [planTasks]);

  // The day strip's bars, and the store clock they sit against.
  const storeNow = dayjs(getStoreNow(storeTimezone));
  let nowQh = storeNow.hour() + storeNow.minute() / 60;
  if (nowQh < startQhnum) nowQh += 24;
  const mapEntries = useMemo(
    () => buildMapEntries(todayPlanTasks, nowQh),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [todayPlanTasks, clockTick]
  );
  // The strip's axis: the task windows, the labor chart's shifts (published
  // and clocked) and the now line; see axisBoundsFor.
  const scheduleBounds = useMemo(
    () => axisBoundsFor(mapEntries, [...(actualSchedule || []), ...(actualUsedSchedule || [])], nowQh),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapEntries, actualSchedule, actualUsedSchedule, clockTick]
  );

  const handleRefreshClick = () => {
    setRefreshing(true);
    fetchData(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: "60vh" }}>
        <LoadSpinner />
      </div>
    );
  }

  // No day-payload empty state here: the Now list is the authority on what
  // can be done (it carries last night's started routine, which today's day
  // payload does not), and NowTable renders its own "nothing to do".
  if (!kiosk) {
    // The manager acts as themselves, through the same drawer the Tasks page
    // opens: row click (or a bar on the day strip) opens it; Assign, Work on
    // it, Validate, the thread and the timeline all live there.
    return (
      <div className="py-4">
        <div className="flex items-center justify-end mb-2">
          <Tooltip title="Refresh tasks. Updates on its own every minute." placement="left">
            <button
              aria-label="Refresh tasks"
              className="text-slate-400 hover:text-slate-600 transition-colors"
              onClick={handleRefreshClick}
              disabled={refreshing}
            >
              <RefreshIcon
                sx={{ fontSize: 18 }}
                className={refreshing ? "animate-spin" : ""}
              />
            </button>
          </Tooltip>
        </div>

        {mapEntries.length > 0 && (
          <DayGanttMap
            entries={mapEntries}
            axisStart={scheduleBounds.start}
            axisEnd={scheduleBounds.end}
            nowQh={nowQh}
            onBarClick={(e) =>
              drawerHost.current?.open(e.task.uuid, e.a?.date || todayStr, e.a?.intraday_slot_index ?? null)
            }
          />
        )}

        {nowError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm text-slate-500">These tasks could not be loaded.</p>
            <button
              onClick={() => fetchData(false)}
              className="text-sm font-semibold text-violet-700 hover:text-violet-800"
            >
              Try again
            </button>
          </div>
        ) : nowData ? (
          <NowTable
            data={nowData}
            storeId={storeId}
            showDone={showDone}
            showUpcoming={showUpcoming}
            onToggleUpcoming={setShowUpcoming}
            onToggleDone={setShowDone}
            onOpenTask={(row, tab) =>
              drawerHost.current?.open(row.plan_task?.uuid, row.date, row.intraday_slot_index, tab)
            }
          />
        ) : (
          <div className="flex justify-center py-16">
            <LoadSpinner />
          </div>
        )}

        <TaskDrawerHost
          ref={drawerHost}
          storeId={storeId}
          homeCompany={homeCompany}
          canManage
          tz={nowData?.timezone}
          todayStr={nowData?.date || todayStr}
          roleNames={roleNames}
          onChanged={() => fetchData(false)}
        />
      </div>
    );
  }

  // Kiosk: the identity gate leads; the strip is the signed-in person's and
  // is drawn by KioskTasksPanel under their list.
  // No refresh button: the person's list and the day payload each refetch
  // once a minute and after every save, and the button did not touch the
  // list anyway. The one control worth a glance here is Done.
  return (
    <div className="py-4">
      <KioskTasksPanel
        storeId={storeId}
        homeCompany={homeCompany}
        roleNames={roleNames}
        planTasks={todayPlanTasks}
        pinRequired={pinRequired}
        onChanged={() => fetchData(false)}
        dayMap={{
          entries: mapEntries,
          axisStart: scheduleBounds.start,
          axisEnd: scheduleBounds.end,
          nowQh,
        }}
      />
    </div>
  );
}

export default LiveActionTasksPanel;
